import React, { useState } from "react";
import { Download, Search, FileText } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
const reports = [{
  year: "2023",
  title: "Expanding Horizons",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/report_cover_2023.png",
  description: "A year of record-breaking growth in entrepreneurship programs.",
  link: "/reports/2023-annual-report.pdf"
}, {
  year: "2022",
  title: "Building Resilience",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/report_cover_2022.png",
  description: "Supporting our community through post-pandemic recovery.",
  link: "/reports/2022-annual-report.pdf"
}, {
  year: "2021",
  title: "Foundations of Change",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/report_cover_2021.png",
  description: "Establishing the core pillars of our mission and vision.",
  link: "/reports/2021-annual-report.pdf"
}, {
  year: "2020",
  title: "Community First",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/report_cover_2020.png",
  description: "Immediate response initiatives for families and small businesses.",
  link: "/reports/2020-annual-report.pdf"
}, {
  year: "2019",
  title: "Inception & Vision",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/report_cover_2019.png",
  description: "The beginning of the COHCC Foundation's journey.",
  link: "/reports/2019-annual-report.pdf"
}, {
  year: "2018",
  title: "Legacy of Leadership",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/report_cover_2018.png",
  description: "Celebrating 40 years of Hispanic Chamber impact.",
  link: "/reports/2018-annual-report.pdf"
}];
export default function AnnualReportArchive() {
  const [searchTerm, setSearchTerm] = useState("");
  const filteredReports = reports.filter(report => report.year.includes(searchTerm) || report.title.toLowerCase().includes(searchTerm.toLowerCase()));
  return <section id="annual-report-archive" className="bg-background py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-[var(--color-secondary)]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-[var(--color-primary)]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-7xl mx-auto px-6 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-end gap-8 mb-16">
          <div className="max-w-2xl">
            <h2 className="text-4xl md:text-5xl font-bold font-serif text-[var(--color-primary)] mb-4">
              Annual Report Archive
            </h2>
            <p className="text-lg text-[var(--color-text-dark)]/80 font-sans max-w-xl">
              Explore our history of impact. Download past annual reports to see how your support has helped us advance prosperity for the Hispanic community in Colorado.
            </p>
          </div>

          <div className="w-full md:w-auto min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input type="text" placeholder="Search by year..." className="pl-10 bg-white border-[var(--color-border)] focus-visible:ring-[var(--color-secondary)]" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
          </div>
        </div>

        {filteredReports.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredReports.map((report, i) => <div key={report.year} data-index={i} className="group perspective-1000">
                <Card className="h-full flex flex-col overflow-hidden border-none shadow-lg transition-all duration-500 ease-out transform group-hover:-rotate-1 group-hover:scale-[1.02] group-hover:shadow-xl group-hover:shadow-[var(--color-secondary)]/20 bg-white">
                  <div className="relative h-64 overflow-hidden bg-[var(--color-primary)]/5">
                    <img src={report.image} alt={`${report.year} Annual Report Cover`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-primary)]/80 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300" />
                    <div className="absolute bottom-4 left-6">
                      <span className="inline-block px-3 py-1 bg-[var(--color-secondary)] text-[var(--color-primary)] text-xs font-bold rounded-full mb-2">
                        {report.year}
                      </span>
                      <h3 className="text-white font-serif text-2xl font-bold leading-tight">
                        {report.title}
                      </h3>
                    </div>
                  </div>
                  
                  <CardContent className="flex-grow pt-6 px-6">
                    <CardDescription className="text-base text-[var(--color-text-dark)] line-clamp-3">
                      {report.description}
                    </CardDescription>
                  </CardContent>

                  <CardFooter className="px-6 pb-6 pt-0">
                    <Button asChild className="w-full bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/90 text-white font-medium shadow-md group-hover:shadow-lg transition-all">
                      <Link to={report.link} target="_blank" rel="noopener noreferrer">
                        <Download className="mr-2 h-4 w-4" />
                        Download PDF
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>)}
          </div> : <div className="text-center py-20 bg-[var(--color-muted)]/30 rounded-xl border border-dashed border-[var(--color-border)]">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-serif font-semibold text-[var(--color-primary)] mb-2">
              No reports found
            </h3>
            <p className="text-muted-foreground">
              We couldn't find any reports matching "{searchTerm}". Please try a different year.
            </p>
            <Button variant="link" onClick={() => setSearchTerm("")} className="mt-4 text-[var(--color-secondary)]">
              Clear search
            </Button>
          </div>}
      </div>
    </section>;
}